import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, fromEvent, merge, of, throwError, timer } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { skipGlobalErrorToast } from './error-notification.interceptor';

/** Snapshot taken by the backend when the notification was created. */
export interface JobMatchNotificationPayload {
  job_id: number;
  similarity: number | null;
  title: string | null;
  company: string | null;
  location: string | null;
  is_remote: boolean;
  employment_type: string | null;
  url: string;
  apply_url: string;
}

export interface AppNotification {
  id: number;
  type: string;
  payload: JobMatchNotificationPayload;
  /** null = unread */
  read_at: string | null;
  created_at: string;
}

interface NotificationListResponse {
  items: AppNotification[];
  total: number;
  unread_count: number;
}

/** How many notifications the bell dropdown loads. */
export const NOTIFICATIONS_PAGE_SIZE = 20;
/** Simple polling, no websockets (chamba-ai#411). */
export const NOTIFICATIONS_POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * In-app notifications behind the header bell (chamba-ai#411): the job
 * offers the user matched with. Backed by /api/notifications.
 *
 * Every call opts out of the generic global error toast: the unread count is
 * polled in the background, and a failing poll must never pop an error in
 * front of the user. Failures keep the last known state instead.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiBaseUrl}/notifications`;

  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private readonly notificationsSubject = new BehaviorSubject<AppNotification[]>([]);
  readonly unreadCount$ = this.unreadCountSubject.asObservable();
  readonly notifications$ = this.notificationsSubject.asObservable();

  private pollSubscription: Subscription | null = null;

  get unreadCount(): number {
    return this.unreadCountSubject.value;
  }

  /**
   * Refreshes the unread count now and then every `intervalMs`. Idempotent
   * (a second call while polling is a no-op). A tick is skipped while the tab
   * is hidden - no point asking the server about a page nobody is looking
   * at - and the count is refreshed the moment the tab becomes visible
   * again, so skipping never leaves it stale.
   */
  startPolling(intervalMs: number = NOTIFICATIONS_POLL_INTERVAL_MS): void {
    if (this.pollSubscription) return;
    const visibilityChanges$ =
      typeof document !== 'undefined' ? fromEvent(document, 'visibilitychange') : of<Event>();
    this.pollSubscription = merge(timer(0, intervalMs), visibilityChanges$)
      .pipe(switchMap(() => (this.isTabHidden() ? of(undefined) : this.refreshUnreadCount())))
      .subscribe();
  }

  private isTabHidden(): boolean {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
  }

  /** Stops polling and forgets everything: the next person to sign in on
   * this same page load must never see the previous user's notifications. */
  stopPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
    this.unreadCountSubject.next(0);
    this.notificationsSubject.next([]);
  }

  refreshUnreadCount(): Observable<void> {
    return this.http
      .get<{ unread_count: number }>(`${this.apiUrl}/unread-count`, { context: skipGlobalErrorToast() })
      .pipe(
        tap((response) => this.unreadCountSubject.next(response.unread_count)),
        map(() => undefined),
        catchError(() => of(undefined))
      );
  }

  /** Loads the newest notifications (and the unread count with them). Errors
   * propagate so the dropdown can show its own error state. */
  loadNotifications(): Observable<void> {
    return this.http
      .get<NotificationListResponse>(this.apiUrl, {
        params: { limit: NOTIFICATIONS_PAGE_SIZE, offset: 0 },
        context: skipGlobalErrorToast(),
      })
      .pipe(
        tap((response) => {
          this.notificationsSubject.next(response.items);
          this.unreadCountSubject.next(response.unread_count);
        }),
        map(() => undefined)
      );
  }

  /** Optimistic: the item shows as read and the badge drops at once; if the
   * request fails only that item and that one unit of the count go back,
   * since the server still has it unread. */
  markAsRead(notification: AppNotification): Observable<void> {
    if (notification.read_at) return of(undefined);

    const readAt = new Date().toISOString();
    this.notificationsSubject.next(
      this.notificationsSubject.value.map((n) => (n.id === notification.id ? { ...n, read_at: readAt } : n))
    );
    this.unreadCountSubject.next(Math.max(0, this.unreadCountSubject.value - 1));

    return this.http
      .post(`${this.apiUrl}/${notification.id}/read`, null, { context: skipGlobalErrorToast() })
      .pipe(
        map(() => undefined),
        catchError((error) => {
          this.notificationsSubject.next(
            this.notificationsSubject.value.map((n) => (n.id === notification.id ? { ...n, read_at: null } : n))
          );
          this.unreadCountSubject.next(this.unreadCountSubject.value + 1);
          return throwError(() => error);
        })
      );
  }

  /** Optimistic like markAsRead. The count goes back to what it was (it may
   * cover more than the loaded page) and only the items this call flipped
   * return to unread. */
  markAllAsRead(): Observable<void> {
    const previousCount = this.unreadCountSubject.value;
    const flippedIds = new Set(this.notificationsSubject.value.filter((n) => !n.read_at).map((n) => n.id));
    const readAt = new Date().toISOString();
    this.notificationsSubject.next(
      this.notificationsSubject.value.map((n) => (flippedIds.has(n.id) ? { ...n, read_at: readAt } : n))
    );
    this.unreadCountSubject.next(0);

    return this.http
      .post(`${this.apiUrl}/read-all`, null, { context: skipGlobalErrorToast() })
      .pipe(
        map(() => undefined),
        catchError((error) => {
          this.notificationsSubject.next(
            this.notificationsSubject.value.map((n) => (flippedIds.has(n.id) ? { ...n, read_at: null } : n))
          );
          this.unreadCountSubject.next(previousCount);
          return throwError(() => error);
        })
      );
  }
}
